import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { orderService } from '../../services/api'

const initialState = {
  orders: [],
  currentOrder: null,
  loading: false,
  error: null,
  checkoutLoading: false,
  checkoutError: null,
  pagination: {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  }
}

// Thunks
export const checkout = createAsyncThunk(
  'orders/checkout',
  async (checkoutData, { rejectWithValue }) => {
    try {
      const response = await orderService.checkout(checkoutData)
      return response.data.obj
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to checkout')
    }
  }
)

export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (filters = {}, { rejectWithValue }) => {
    try {
      console.log('🔄 fetchOrders: Iniciando requisição com filtros:', filters)
      
      // Filtrar parâmetros vazios e preparar filtros para paginação
      const cleanFilters = Object.fromEntries( 
        Object.entries(filters).filter(([, value]) => {
          if (value === null || value === undefined || value === '') return false
          return true
        })
      )
      
      const response = await orderService.getOrders(cleanFilters)
      console.log('📦 fetchOrders: response.data:', response.data)
      
      // IMPORTANTE: Retornar apenas response.data.obj que contém { orders: [...], pagination: {...} }
      const result = response.data.obj
      console.log('📦 fetchOrders: Retornando resultado:', result)
      return result
    } catch (error) {
      console.error('❌ fetchOrders: Erro na requisição:', error)
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch orders')
    }
  }
)

export const fetchOrderById = createAsyncThunk(
  'orders/fetchOrderById',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await orderService.getOrder(orderId)
      return response.data.obj
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch order')
    }
  }
)

const orderSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
      state.checkoutError = null
    },
    clearCurrentOrder: (state) => {
      state.currentOrder = null
    },
    setCurrentOrder: (state, action) => {
      state.currentOrder = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      // Checkout
      .addCase(checkout.pending, (state) => {
        state.checkoutLoading = true
        state.checkoutError = null
      })
      .addCase(checkout.fulfilled, (state, action) => {
        state.checkoutLoading = false
        state.currentOrder = action.payload.order
        // Adicionar o pedido à lista se não existir
        const existingOrderIndex = state.orders.findIndex(order => order.id === action.payload.order.id)
        if (existingOrderIndex === -1) {
          state.orders.unshift(action.payload.order)
        }
      })
      .addCase(checkout.rejected, (state, action) => {
        state.checkoutLoading = false
        state.checkoutError = action.payload
      })
      
      // Fetch Orders
      .addCase(fetchOrders.pending, (state) => {
        console.log('⏳ fetchOrders.pending: Iniciando carregamento')
        state.loading = true
        state.error = null
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        console.log('✅ fetchOrders.fulfilled: REDUCER EXECUTADO!')
        console.log('✅ fetchOrders.fulfilled: Payload recebido:', action.payload)
        
        state.loading = false
        
        // Verificar se temos orders no payload
        if (action.payload && action.payload.orders && Array.isArray(action.payload.orders)) {
          console.log('✅ Usando action.payload.orders:', action.payload.orders)
          state.orders = [...action.payload.orders] // Forçar nova referência
        } else {
          console.log('⚠️ Formato inesperado do payload, usando array vazio')
          state.orders = []
        }
        
        state.pagination = action.payload?.pagination || state.pagination
        
        console.log('✅ fetchOrders.fulfilled: state.orders final:', state.orders)
        console.log('✅ fetchOrders.fulfilled: state.orders.length:', state.orders.length)
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        console.log('❌ fetchOrders.rejected: Erro no carregamento:', action.payload)
        state.loading = false
        state.error = action.payload
      })
      
      // Fetch Order by ID
      .addCase(fetchOrderById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.loading = false
        state.currentOrder = action.payload.order
        // Atualizar o pedido na lista se existir
        const orderIndex = state.orders.findIndex(order => order.id === action.payload.order.id)
        if (orderIndex !== -1) {
          state.orders[orderIndex] = action.payload.order
        }
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const { clearError, clearCurrentOrder, setCurrentOrder } = orderSlice.actions

// Selectors
export const selectOrders = (state) => state.orders?.orders || []
export const selectCurrentOrder = (state) => state.orders?.currentOrder || null
export const selectOrdersLoading = (state) => state.orders?.loading || false
export const selectOrdersError = (state) => state.orders?.error || null
export const selectCheckoutLoading = (state) => state.orders?.checkoutLoading || false
export const selectCheckoutError = (state) => state.orders?.checkoutError || null
export const selectOrdersPagination = (state) => state.orders?.pagination || {
  current_page: 1,
  last_page: 1,
  per_page: 15,
  total: 0
}

// Selector para pedido específico
export const selectOrderById = (orderId) => (state) => {
  const orders = state.orders?.orders || []
  return orders.find(order => order.id === orderId) || null
}

// Selector para pedidos por status
export const selectOrdersByStatus = (status) => (state) => {
  const orders = state.orders?.orders || []
  if (!status || status === 'all') return orders
  return orders.filter(order => order.status === status)
}

// Status constants (conforme documentação da API)
export const ORDER_STATUS = {
  PENDING: 1,
  PROCESSING: 2,
  COMPLETED: 3,
  CANCELLED: 4
}

export const ORDER_STATUS_NAMES = {
  1: 'PENDING',
  2: 'PROCESSING', 
  3: 'COMPLETED',
  4: 'CANCELLED'
}

export const PAYMENT_METHODS = {
  PAGSEGURO: 'pagseguro',
  PAYPAL: 'paypal',
  PIX: 'pix',
  PHYSICAL: 'physical',
  DENTAL_PREV: 'dental_prev'
}

export default orderSlice.reducer